import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import OpenInNewTabIcon from 'transfix-icons/build/OpenInNewTabIcon'
import { Skeleton, Link } from 'transfix-ui/core'
import { ICarrierPaymentBill } from 'types'
import { fetchMarketplaceLoad } from 'utils/api/marketplace'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { LoadStatusFunctionalTag } from 'apps/fleet-planner/components/atoms/LoadStatusFunctionalTag'
import { LoadStatusFunctionalTagMarketplace } from 'apps/fleet-planner/components/atoms/LoadStatusFunctionalTagMarketplace'
import { LoadStatusFunctionalTagMobile } from 'apps/fleet-planner/components/atoms/LoadStatusTagMobile'
import { CarrierInlineEdit } from 'apps/fleet-planner/components/form-components/CarrierInlineEdit'
import { DispatcherInlineEdit } from 'apps/fleet-planner/components/form-components/DispatcherInlineEdit'
import { DriversSingleInlineEdit } from 'apps/fleet-planner/components/form-components/DriversInlineEdit'
import { TrailerInlineEdit } from 'apps/fleet-planner/components/form-components/TrailerInlineEdit'
import { TruckInlineEdit } from 'apps/fleet-planner/components/form-components/TruckInlineEdit'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  ICarrier,
  IDispatcher,
  IDriver,
  ILoadStatusOption,
  ITodo,
  ITrailer,
  ITruck,
  IStop,
  ICustomerContact,
  ILoadSettings,
  ILoad,
} from 'apps/fleet-planner/types'
import { IExternalEntities } from 'apps/fleet-planner/types/entity'
import { IExternalPermissions } from 'apps/fleet-planner/types/permissions'
import { areRequiredMpStopsValid } from 'apps/fleet-planner/utils/checkinCheckoutTimesHelpers'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { Card, Toast, useToastState, ConfirmationModal, IModalAction } from 'transfix-ui'
import { generateBaseUrl } from 'utils/generateAppDefaults'
import { LoadStatusFunctionalTagMarketplaceMobile } from '../atoms/LoadStatusTagMarketplaceMobile'
import { LoadAssignmentInlineEdit } from './LoadAssignmentInlineEdit'
import { PaymentStatus } from './PaymentStatus'
import { EPaymentState } from './constants'
import { SidebarLabel } from './sidebar'
import { SidebarItem } from './sidebar/SidebarItem'
import { LoadSubscribers } from './subscribers/LoadSubscribers'

interface IStatusProps {
  id?: string
  drivers?: IDriver[]
  dispatcher?: IDispatcher
  carrier?: ICarrier
  trailer: Partial<ITrailer>
  truck: Partial<ITruck>
  status: LOAD_STATE
  stops: IStop[]
  currentStopSequence?: string
  inlineEditField?: string
  refetchOptions?: boolean
  isStatusUpdating?: boolean
  externalEntities?: IExternalEntities
  permissions?: IExternalPermissions
  isExternal?: boolean
  subscribers?: ICustomerContact[]
  settings?: ILoadSettings
  customerContact?: ICustomerContact
  onStatusUpdate: (status: ILoadStatusOption) => void
  updateLoad: (changes: Partial<ILoad>) => void
  setInlineEditField: (name?: string) => void
  tractorVinNumber?: string
  onTractorVinUpdate: (tractorVin: string) => void
  externalId?: string
  setAndScrollToField?: (name?: string, elementId?: string) => void
  currentShipmentTodos?: ITodo
  bills?: ICarrierPaymentBill[]
}

export const LoadStatus = memo<IStatusProps>(
  ({
    id,
    onStatusUpdate,
    updateLoad,
    drivers = [],
    dispatcher,
    carrier,
    trailer,
    truck,
    status,
    stops,
    currentStopSequence,
    inlineEditField,
    setInlineEditField,
    refetchOptions,
    isStatusUpdating,
    externalEntities,
    isExternal,
    permissions,
    subscribers = [],
    settings,
    customerContact,
    externalId,
    setAndScrollToField,
    currentShipmentTodos,
    bills,
  }) => {
    const { load, loading: fpLoading } = useSelector(loadDetailsSelector)
    const { isMobile } = useBreakpointContext()
    const history = useHistory()
    const { pathname = '' } = useLocation()
    const [cancelConfirmationModalStatus, setCancelConfirmationModalStatus] = useState<
      ILoadStatusOption
    >()
    const { trackEvent } = useFMSAnalytics()

    const { isExternalDispatcher, isExternalDriver, isExternalTrailer, isExternalTruck } =
      externalEntities || {}

    const { closeToast, openToast, toastMessage, toastType } = useToastState()
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
    const { enabled: isFmsDetentionRequestEnabled } = getFlag('fmsDetentionRequest')
    const { enabled: isHomePageUpdatesEnabled } = getFlag('homePageUpdates')
    const { enabled: isPaymentStatusEnabled } = getFlag('fmsPaymentStatus')

    const validBills =
      bills?.filter(bill => Object.values(EPaymentState).includes(bill.state)) || []

    const statusChangeHandler = (newLoadStatusOption: ILoadStatusOption) => {
      const { status: newStatus } = newLoadStatusOption
      if (newStatus === LOAD_STATE.Cancelled) {
        setCancelConfirmationModalStatus(newLoadStatusOption)
      } else {
        onStatusUpdate(newLoadStatusOption)
      }
    }

    // There is a marketplaceStatusChangeHandler in src/apps/fleet-planner/components/loads/LoadsTableRow.tsx as well
    const marketplaceStatusChangeHandler = async ({
      currentStopSequence: newCurrentStopSequence,
      status: newStatus,
    }: ILoadStatusOption) => {
      // Prevent status updates if there isn't a driver assigned to the load
      if (!drivers.length) {
        const query = queryString.stringify({
          modalMessageKey: 'addDriverAlert',
        })
        history.push(`${pathname}?${query}`)
        return openToast({
          toastType: 'alert',
          toastMessage: `Please assign a driver before updating this load's status.`,
        })
      }
      // Fetch marketplace load for manual eta if load is external
      if (isExternal && externalId) {
        const marketplaceId = externalId.replace('TFX-', '')
        const { shipment: marketplaceShipment } = await fetchMarketplaceLoad(marketplaceId)

        const { status: marketplaceStatus, stops: marketplaceStops = [] } =
          marketplaceShipment || {}
        const { manualEta } = marketplaceStatus || {}

        // If shipment status is being transitioned to dispatched
        if (newStatus === LOAD_STATE.Dispatched) {
          if (!manualEta) {
            // Open pickup ETA modal if there is no manual eta
            return history.push(`${pathname}/update-eta`)
          }
          // Otherwise dispatch the shipment
          return statusChangeHandler({
            status: newStatus,
            currentStopSequence: newCurrentStopSequence,
          })
        }

        // If marketplace already has the required checkin or checkout times, skip the Checkin and Checkout modal and just update the status.
        const areMpStopTimesAlreadyPresent = areRequiredMpStopsValid(
          marketplaceStops,
          newStatus as LOAD_STATE,
          newCurrentStopSequence || ''
        )
        if (areMpStopTimesAlreadyPresent) {
          return statusChangeHandler({
            status: newStatus,
            currentStopSequence: newCurrentStopSequence,
          })
        }
      }

      // For any other state transitions open the Checkin and Checkout modal with status and stopSequence params
      const query = queryString.stringify({
        status: newStatus,
        stopSequence: newCurrentStopSequence,
      })
      return history.push(`${pathname}/update-status?${query}`)
    }

    const onModalClose = (action?: string) => {
      if (action === 'confirm' && cancelConfirmationModalStatus) {
        onStatusUpdate(cancelConfirmationModalStatus)
      }
      setCancelConfirmationModalStatus(undefined)
    }

    const modalActions: IModalAction[] = [
      {
        label: "Don't Cancel",
        type: 'dismiss',
      },
      {
        label: 'Cancel Load',
        type: 'confirm',
      },
    ]

    const shouldShowDetentionLink = isFmsDetentionRequestEnabled && isExternal && externalId
    const detentionLink = externalId
      ? generateBaseUrl(`fms/detention-request/${externalId.replace('TFX-', '')}`)
      : ''

    const triumphPayClickHandler = () =>
      trackEvent('Load', 'CLICKED_TRIUMPHPAY_LINK_FROM_LOAD_DETAILS')

    const renderDriversAndSubscribers = () =>
      isMobile ? (
        <Card className='mt4 mb4'>
          <SidebarLabel BoxProps={{ pb: 1.5 }}>Status</SidebarLabel>
          {fpLoading && <Skeleton variant='rectangular' height={20} width={190} />}
          {!fpLoading && (
            <>
              {isExternal && isFpCarrierPortalEnabled ? (
                <LoadStatusFunctionalTagMarketplaceMobile
                  status={status}
                  currentStopSequence={Number(currentStopSequence)}
                  onChange={marketplaceStatusChangeHandler}
                  stops={stops}
                  dataTest='status-select'
                  isLoading={isStatusUpdating}
                />
              ) : (
                <LoadStatusFunctionalTagMobile
                  status={status}
                  currentStopSequence={Number(currentStopSequence)}
                  onChange={statusChangeHandler}
                  stops={stops}
                  dataTest='status-select-mobile'
                  isLoading={isStatusUpdating}
                />
              )}
              <LoadSubscribers
                drivers={drivers}
                subscribers={subscribers}
                loadSettings={settings}
                isExternal={isExternal}
                customerContact={customerContact}
                onUnassignedDriverClick={setAndScrollToField}
                showDriverStatus={!isExternal}
              />
            </>
          )}
        </Card>
      ) : (
        <>
          <SidebarItem>
            <SidebarLabel BoxProps={{ pb: 1.5 }}>Status</SidebarLabel>
            {fpLoading && <Skeleton variant='rectangular' height={20} width={190} />}
            {!fpLoading && (
              <>
                {isExternal && isFpCarrierPortalEnabled ? (
                  <LoadStatusFunctionalTagMarketplace
                    status={status}
                    currentStopSequence={Number(currentStopSequence)}
                    onChange={marketplaceStatusChangeHandler}
                    stops={stops}
                    dataTest='status-select'
                    isLoading={isStatusUpdating}
                  />
                ) : (
                  <LoadStatusFunctionalTag
                    status={status}
                    currentStopSequence={Number(currentStopSequence)}
                    onChange={statusChangeHandler}
                    stops={stops}
                    dataTest='status-select'
                    isLoading={isStatusUpdating}
                  />
                )}
                {status === LOAD_STATE.Completed && (
                  <div className='flex flex-row items-center mt2 mb3'>
                    <div className='pr2'>
                      <Link
                        target='_blank'
                        rel='noopener noreferrer'
                        href={`${__ROOT__}/payments`}
                        variant='body2'>
                        Check the status of your payment
                      </Link>
                    </div>
                    <OpenInNewTabIcon color='skyBlue6' />
                  </div>
                )}
              </>
            )}
          </SidebarItem>
          <LoadSubscribers
            drivers={drivers}
            subscribers={subscribers}
            loadSettings={settings}
            isExternal={isExternal}
            customerContact={customerContact}
            onUnassignedDriverClick={setAndScrollToField}
            showDriverStatus={!isExternal}
          />
        </>
      )

    const renderLoadStatusInfo = () => {
      const ParentComponent = isMobile ? Card : 'span'
      return (
        <ParentComponent>
          {/* Drivers */}
          {!isFpCarrierPortalEnabled && (
            <DriversSingleInlineEdit
              isExternal={isExternalDriver}
              isExternalLoad={isExternal}
              value={drivers}
              currentEditField={inlineEditField}
              onEditModeRequested={setInlineEditField}
              onUpdate={updateLoad}
              refetchOptions={refetchOptions}
            />
          )}

          {/* Dispatcher */}
          {!isFpCarrierPortalEnabled && (
            <DispatcherInlineEdit
              isExternalDispatcher={isExternalDispatcher}
              dispatcher={dispatcher}
              currentEditField={inlineEditField}
              onEditModeRequested={setInlineEditField}
            />
          )}

          {/* Truck */}
          {!isFpCarrierPortalEnabled && (
            <TruckInlineEdit
              isExternalTruck={isExternalTruck}
              id={id}
              truck={truck as ITruck}
              currentEditField={inlineEditField}
              onEditModeRequested={setInlineEditField}
            />
          )}

          {/* Trailer */}
          {!isFpCarrierPortalEnabled && (
            <TrailerInlineEdit
              isExternalTrailer={isExternalTrailer}
              id={id}
              onUpdate={updateLoad}
              trailer={trailer as ITrailer}
              currentEditField={inlineEditField}
              onEditModeRequested={setInlineEditField}
              carrier={carrier}
            />
          )}

          {isFpCarrierPortalEnabled && (
            <>
              <SidebarItem BoxProps={{ pt: 3 }}>
                <SidebarLabel BoxProps={{ pb: 0.5 }}>Assignment</SidebarLabel>
                <LoadAssignmentInlineEdit
                  value={load}
                  currentEditField={inlineEditField}
                  onEditModeRequested={setInlineEditField}
                  onUpdate={updateLoad}
                  currentShipmentTodos={currentShipmentTodos}
                  dataTest='inline-edit-assignment'
                />
              </SidebarItem>
              {isPaymentStatusEnabled && validBills.length > 0 && (
                <PaymentStatus bills={validBills} />
              )}
              {isHomePageUpdatesEnabled && isExternal && (
                <SidebarItem>
                  <SidebarLabel BoxProps={{ pb: 0.5 }}>
                    <Link
                      onClick={triumphPayClickHandler}
                      target='_blank'
                      rel='noopener noreferrer'
                      href='https://secure.triumphpay.com/'>
                      <OpenInNewTabIcon
                        color='skyBlue6'
                        size='large'
                        verticalAlign='text-top'
                        className='mr2'
                      />
                      TriumphPay
                    </Link>
                  </SidebarLabel>
                </SidebarItem>
              )}
              {shouldShowDetentionLink && (
                <SidebarItem BoxProps={{ pt: 3 }}>
                  <SidebarLabel BoxProps={{ pb: 0.5 }}>
                    <Link target='_blank' rel='noopener noreferrer' href={detentionLink}>
                      Request Detention
                    </Link>
                  </SidebarLabel>
                </SidebarItem>
              )}
            </>
          )}
          {/* Carrier */}
          {!isFpCarrierPortalEnabled && (
            <CarrierInlineEdit
              isLocked={permissions?.carrier}
              onUpdate={updateLoad}
              carrier={carrier}
              currentEditField={inlineEditField}
              onEditModeRequested={setInlineEditField}
            />
          )}
        </ParentComponent>
      )
    }

    return (
      <>
        <div>
          {renderDriversAndSubscribers()}
          {renderLoadStatusInfo()}
        </div>
        {!!cancelConfirmationModalStatus && (
          <ConfirmationModal
            title='Cancel load?'
            size={4}
            open={!!cancelConfirmationModalStatus}
            actions={modalActions}
            onClose={onModalClose}
            dataTest='cancel-load'>
            <>This load will be moved to the Archived tab of the Loads page.</>
          </ConfirmationModal>
        )}
        <Toast
          open={!!toastMessage}
          onClose={closeToast}
          message={toastMessage}
          variation={toastType}
        />
      </>
    )
  }
)
